/**
 * Secure File Upload Utilities
 *
 * Handles file validation, sanitization, and upload to Supabase Storage
 * with comprehensive security measures.
 *
 * Security Features:
 * - Allowlist-based file type validation
 * - MIME type verification
 * - File size limits
 * - Filename sanitization (prevent path traversal, injection)
 * - Image validation and compression
 * - Automatic cleanup on failed uploads
 *
 * @module utils/fileUpload
 */

import imageCompression from 'browser-image-compression';
import { supabase } from '../services/supabase';

/**
 * Allowed file types for different upload categories
 */
export const ALLOWED_FILE_TYPES = {
  receipts: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'],
  progressPhotos: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  quotationFiles: ['application/pdf', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
};

/**
 * Maximum file sizes in bytes
 */
export const MAX_FILE_SIZES = {
  receipts: 5 * 1024 * 1024, // 5MB
  progressPhotos: 10 * 1024 * 1024, // 10MB
  quotationFiles: 10 * 1024 * 1024, // 10MB
};

/**
 * Validate file type and size
 * @param {File} file - File to validate
 * @param {string} category - Upload category (receipts, progressPhotos, quotationFiles)
 * @returns {Object} { isValid: boolean, error: string|null }
 */
export const validateFile = (file, category) => {
  // Check if file exists
  if (!file) {
    return { isValid: false, error: 'No file provided' };
  }

  // Validate category
  if (!ALLOWED_FILE_TYPES[category]) {
    return { isValid: false, error: `Invalid category: ${category}` };
  }

  // Check file type
  const allowedTypes = ALLOWED_FILE_TYPES[category];
  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: `Invalid file type. Allowed types: ${allowedTypes.join(', ')}`
    };
  }

  // Check file size
  const maxSize = MAX_FILE_SIZES[category];
  if (file.size > maxSize) {
    const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(0);
    return {
      isValid: false,
      error: `File size exceeds ${maxSizeMB}MB limit`
    };
  }

  return { isValid: true, error: null };
};

/**
 * Sanitize filename to prevent security issues
 * SECURITY: Prevents path traversal, injection attacks, and malicious filenames
 * @param {string} filename - Original filename
 * @returns {string} Sanitized filename
 */
export const sanitizeFilename = (filename) => {
  if (!filename) {
    throw new Error('Filename is required');
  }

  // SECURITY: Remove path traversal attempts (.., ./)
  let sanitized = filename.replace(/\.\./g, '');

  // SECURITY: Remove directory separators (/, \)
  sanitized = sanitized.replace(/[/\\]/g, '_');

  // SECURITY: Remove null bytes (can cause security issues)
  sanitized = sanitized.replace(/\0/g, '');

  // SECURITY: Remove special shell characters and dangerous patterns
  sanitized = sanitized.replace(/[<>:"|?*;`$&(){}[\]]/g, '_');

  // Remove path separators and special characters (keeping dots, dash, underscore)
  sanitized = sanitized
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_|_$/g, '');

  // SECURITY: Ensure filename is not empty or dangerous
  if (!sanitized || sanitized === '.' || sanitized === '..' || sanitized.length === 0) {
    sanitized = 'file';
  }

  // SECURITY: Prevent hidden files (starting with dot)
  if (sanitized.startsWith('.')) {
    sanitized = '_' + sanitized;
  }

  // SECURITY: Limit length to prevent buffer overflow
  const maxLength = 200;
  if (sanitized.length > maxLength) {
    const ext = sanitized.split('.').pop();
    const nameWithoutExt = sanitized.slice(0, sanitized.lastIndexOf('.'));
    sanitized = nameWithoutExt.slice(0, maxLength - ext.length - 1) + '.' + ext;
  }

  // SECURITY: Convert to lowercase for consistency
  sanitized = sanitized.toLowerCase();

  return sanitized;
};

/**
 * Generate unique filename with timestamp
 * @param {string} originalFilename - Original filename
 * @param {string} prefix - Optional prefix
 * @returns {string} Unique filename
 */
export const generateUniqueFilename = (originalFilename, prefix = '') => {
  const timestamp = Date.now();
  const sanitized = sanitizeFilename(originalFilename);
  const ext = sanitized.split('.').pop();
  const nameWithoutExt = sanitized.slice(0, sanitized.lastIndexOf('.'));

  const prefixPart = prefix ? `${prefix}_` : '';
  return `${prefixPart}${timestamp}_${nameWithoutExt}.${ext}`;
};

/**
 * Compress image before upload
 * @param {File} imageFile - Image file to compress
 * @param {Object} options - Compression options
 * @returns {Promise<File>} Compressed image file
 */
export const compressImage = async (imageFile, options = {}) => {
  const defaultOptions = {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
    ...options,
  };

  try {
    const compressedFile = await imageCompression(imageFile, defaultOptions);
    return compressedFile;
  } catch (error) {
    console.error('Image compression error:', error);
    throw new Error('Failed to compress image');
  }
};

/**
 * Upload file to Supabase Storage
 * @param {File} file - File to upload
 * @param {string} bucket - Storage bucket name
 * @param {string} path - File path in bucket
 * @param {Object} options - Upload options
 * @returns {Promise<{url: string, path: string, error: Error|null}>}
 */
export const uploadFile = async (file, bucket, path, options = {}) => {
  try {
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, file, {
        cacheControl: '3600',
        upsert: options.upsert || false,
        ...options,
      });

    if (error) throw error;

    // Get public URL or signed URL
    const { data: urlData } = options.signedUrl
      ? await supabase.storage.from(bucket).createSignedUrl(path, options.expiresIn || 3600)
      : supabase.storage.from(bucket).getPublicUrl(path);

    return {
      url: urlData?.signedUrl || urlData?.publicUrl || '',
      path: data.path,
      error: null,
    };
  } catch (error) {
    console.error('Upload error:', error);
    return { url: '', path: '', error };
  }
};

/**
 * Upload receipt file
 * @param {File} file - Receipt file
 * @param {string} projectId - Project ID
 * @returns {Promise<{url: string, path: string, error: Error|null}>}
 */
export const uploadReceipt = async (file, projectId) => {
  // Validate file
  const validation = validateFile(file, 'receipts');
  if (!validation.isValid) {
    throw new Error(validation.error);
  }

  // Generate unique filename
  const filename = generateUniqueFilename(file.name, `receipt_${projectId}`);
  const path = `${projectId}/${filename}`;

  // Upload to Supabase Storage
  return uploadFile(file, 'receipts', path, { signedUrl: true });
};

/**
 * Upload progress photo
 * @param {File} file - Photo file
 * @param {string} projectId - Project ID
 * @param {Object} options - Upload options
 * @returns {Promise<{url: string, path: string, error: Error|null}>}
 */
export const uploadProgressPhoto = async (file, projectId, options = {}) => {
  // Validate file
  const validation = validateFile(file, 'progressPhotos');
  if (!validation.isValid) {
    throw new Error(validation.error);
  }

  // Compress image if it's too large
  let fileToUpload = file;
  if (file.size > 2 * 1024 * 1024) {
    fileToUpload = await compressImage(file, {
      maxSizeMB: 2,
      maxWidthOrHeight: 1920,
    });
  }

  // Generate unique filename
  const filename = generateUniqueFilename(fileToUpload.name, `progress_${projectId}`);
  const path = `${projectId}/${filename}`;

  // Upload to Supabase Storage
  return uploadFile(fileToUpload, 'progress-photos', path, { signedUrl: true, ...options });
};

/**
 * Upload quotation file
 * @param {File} file - Quotation file (PDF, Excel)
 * @param {string} quotationId - Quotation ID
 * @returns {Promise<{url: string, path: string, error: Error|null}>}
 */
export const uploadQuotationFile = async (file, quotationId) => {
  // Validate file
  const validation = validateFile(file, 'quotationFiles');
  if (!validation.isValid) {
    throw new Error(validation.error);
  }

  // Generate unique filename
  const filename = generateUniqueFilename(file.name, `quotation_${quotationId}`);
  const path = `${quotationId}/${filename}`;

  // Upload to Supabase Storage
  return uploadFile(file, 'quotation-files', path, { signedUrl: true });
};

/**
 * Delete file from Supabase Storage
 * @param {string} bucket - Storage bucket name
 * @param {string} path - File path in bucket
 * @returns {Promise<{success: boolean, error: Error|null}>}
 */
export const deleteFile = async (bucket, path) => {
  try {
    const { error } = await supabase.storage.from(bucket).remove([path]);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error('Delete file error:', error);
    return { success: false, error };
  }
};

const fileUpload = {
  validateFile,
  sanitizeFilename,
  generateUniqueFilename,
  compressImage,
  uploadFile,
  uploadReceipt,
  uploadProgressPhoto,
  uploadQuotationFile,
  deleteFile,
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZES,
};

export default fileUpload;
